import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { OutlineArrowRight } from '../../../components/icons/outline-arrow-right';
import { breakPoint } from '../../../app/theme';
import { SettingsMenu } from '../../../features/settings/components/settings-menu';

import styles from './user-settings-page.module.scss';

type MenuOptions = {
  key: string;
  label: string;
  href: string;
  external?: boolean;
};

type SettingsLayoutProps = React.PropsWithChildren<{
  title: string;
  menu: MenuOptions[];
}>;

export const UserSettingsPageLayout: React.FC<SettingsLayoutProps> = ({
  children,
  title,
  menu,
}) => {
  const navigate = useNavigate();
  const location = useLocation() as { state: { prevPath: string } };

  const goBack = () => {
    if (location.state && location.state.prevPath) {
      navigate(location.state.prevPath);
    } else {
      navigate(`/`);
    }
  };

  return (
    <Wrapper>
      <div className={`desktop-only ${styles['navigation-bar']}`}>
        <Header>
          <BackButton onClick={goBack}>
            <OutlineArrowRight />

            <span>Back</span>
          </BackButton>

          <SettingsTitle>{title}</SettingsTitle>
        </Header>

        <MenuContainer>
          <SettingsMenu items={menu} />
        </MenuContainer>
      </div>

      <Content>
        <ContentContainer>
          <Screens>{children}</Screens>
        </ContentContainer>
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  max-height: 100%;
  height: 100%;
  width: 100%;
`;

const Content = styled.div`
  flex: 1;

  @media (min-width: ${breakPoint.medium}px) {
    padding: 0.4rem 0.4rem 0.4rem 0;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  background: var(--content-bg-color);
  overflow: auto;

  @media (min-width: ${breakPoint.medium}px) {
    border: 1px var(--content-border-color) solid;
    border-radius: 4px;
  }
`;

const Screens = styled.div`
  width: 100%;
  max-width: 64rem;
  margin: 6.4rem auto;
`;

const MenuContainer = styled.div`
  padding: 0 1.6rem;
  flex: 1;
  width: 100%;
`;

const SettingsTitle = styled.h1`
  font-style: normal;
  font-weight: 500;
  font-size: 1.6rem;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  margin: 2.4rem 0 1.6rem 2.4rem;
  color: var(--text-color-white);
`;

export const BackButton = styled.a`
  font-size: 1.4rem;
  line-height: 1.6rem;
  color: #7a8296;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.1s ease-out 0s;

  svg {
    margin-right: 0.6rem;
    width: 1.2rem;
    height: 1.2rem;
    fill: #7a8296;
    transform: rotate(180deg);
  }

  span {
    font-weight: 500;
    font-size: 1.4rem;
    line-height: 1.69rem;
  }

  &:hover {
    cursor: pointer;
    color: #fff;

    svg {
      fill: #fff;
    }
  }
`;
